/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import {
  getIncident,
  getRelatedOperationalMemory,
  updateIncident,
  uploadIncidentAudio,
  uploadIncidentEvidence,
  submitIncidentForProcessing,
  getIncidentNcrDraft,
  approveIncident,
  rejectIncident,
  deleteIncidentEvidence,
  deleteIncidentVoiceCapture,
} from "../api/incidents";
import { completeGuidedSample } from "../api/onboarding";

import { NcrReviewModal } from "../components";
import * as workItemsApi from "../api/workItems";

export default function IncidentWorkspaceDetail() {
  const { incidentId } = useParams();
  const navigate = useNavigate();

  const evidenceInputRef = useRef(null);

  const [incident, setIncident] = useState(null);
  const [relatedMemory, setRelatedMemory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [operatorNotes, setOperatorNotes] = useState("");
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [audioReviewed, setAudioReviewed] = useState(false);
  const [micStatus, setMicStatus] = useState("idle");
  const [micMessage, setMicMessage] = useState("");
  const [audioHealth, setAudioHealth] = useState({
    maxRms: 0,
    speechFrames: 0,
    noiseFrames: 0,
    silentFrames: 0,
  });
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [showNcrReview, setShowNcrReview] = useState(false);

  const [reviewComments, setReviewComments] = useState("");
  const [reviewing, setReviewing] = useState(false);

  const [workItems, setWorkItems] = useState([]);
  const [workItemsLoading, setWorkItemsLoading] = useState(false);
  const [workItemsError, setWorkItemsError] = useState("");
  const [selectedWorkItem, setSelectedWorkItem] = useState(null);
  const [workItemDrawerLoading, setWorkItemDrawerLoading] = useState(false);
  const [workItemDrawerError, setWorkItemDrawerError] = useState("");

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioUrlRef = useRef("");
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const monitorIntervalRef = useRef(null);
  const audioHealthRef = useRef({
    maxRms: 0,
    speechFrames: 0,
    noiseFrames: 0,
    silentFrames: 0,
  });

  const INCIDENT_CAPTURE_DB_NAME = "fluxonix_incident_capture_db";
  const INCIDENT_CAPTURE_DB_VERSION = 1;
  const INCIDENT_AUDIO_DRAFT_STORE = "incident_audio_drafts";

  const workflowGuidanceChecklist = {
    "Design": [
      "Part, product, or system affected",
      "Design change or concern",
      "Reason for the change",
      "Engineering question or decision needed",
      "Photos, drawings, or review evidence attached",
    ],

    "Incoming Material Inspection": [
      "Supplier name",
      "Material / part number",
      "PO, lot, batch, or heat number",
      "Quantity received and quantity affected",
      "Defect or mismatch observed",
      "Disposition: accept, hold, reject, or escalate",
    ],

    "Supplier Quality Containment": [
      "Supplier name",
      "Affected material or part",
      "Defect, risk, or non-conformance",
      "Quantity impacted",
      "Containment action taken",
      "Next owner or follow-up needed",
    ],

    "Production Quality Escalation": [
      "Production line or workstation",
      "Part, batch, or job affected",
      "Issue observed",
      "Quantity impacted",
      "Immediate action taken",
      "Who was notified",
    ],

    "Packaging / Shipping Damage": [
      "Shipment, PO, or delivery reference",
      "Packaging condition",
      "Items damaged",
      "Quantity affected",
      "Carrier, supplier, or customer if known",
      "Photos attached",
    ],

    "Equipment / Downtime Incident": [
      "Machine or equipment name",
      "Location or production line",
      "Failure symptom",
      "Downtime start time",
      "Production impact",
      "Maintenance action needed",
    ],

    "Other": [
      "What happened",
      "Where it happened",
      "Who or what was involved",
      "Why it matters",
      "Action needed next",
    ],
  };

  function getFileExtension(mimeType) {
    if (mimeType.includes("webm")) return "webm";
    if (mimeType.includes("mp4")) return "mp4";
    if (mimeType.includes("m4a")) return "m4a";
    return "webm";
  }

  function openIncidentCaptureDb() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(
        INCIDENT_CAPTURE_DB_NAME,
        INCIDENT_CAPTURE_DB_VERSION
      );

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(INCIDENT_AUDIO_DRAFT_STORE)) {
          db.createObjectStore(INCIDENT_AUDIO_DRAFT_STORE, { keyPath: "incidentId" });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async function saveIncidentAudioDraft(blob, health) {
    const db = await openIncidentCaptureDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(INCIDENT_AUDIO_DRAFT_STORE, "readwrite");
      tx.objectStore(INCIDENT_AUDIO_DRAFT_STORE).put({
        incidentId,
        blob,
        mimeType: blob.type || "audio/webm",
        audioHealth: health,
        createdAt: new Date().toISOString(),
      });
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  }

  async function loadIncidentAudioDraft() {
    const db = await openIncidentCaptureDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(INCIDENT_AUDIO_DRAFT_STORE, "readonly");
      const request = tx.objectStore(INCIDENT_AUDIO_DRAFT_STORE).get(incidentId);
      request.onsuccess = () => {
        db.close();
        resolve(request.result);
      };
      request.onerror = () => {
        db.close();
        reject(request.error);
      };
    });
  }

  async function clearIncidentAudioDraft() {
    const db = await openIncidentCaptureDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(INCIDENT_AUDIO_DRAFT_STORE, "readwrite");
      tx.objectStore(INCIDENT_AUDIO_DRAFT_STORE).delete(incidentId);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  }

  function replaceAudioUrl(nextUrl = "") {
    if (audioUrlRef.current && audioUrlRef.current !== nextUrl) {
      URL.revokeObjectURL(audioUrlRef.current);
    }

    audioUrlRef.current = nextUrl;
    setAudioUrl(nextUrl);
  }

  function updateAudioHealth(nextHealth) {
    audioHealthRef.current = nextHealth;
    setAudioHealth(nextHealth);
  }

  function startAudioHealthMonitor(stream) {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    analyser.fftSize = 2048;
    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.fftSize);
    let maxRms = 0;
    let speechFrames = 0;
    let noiseFrames = 0;
    let silentFrames = 0;
    let elapsedFrames = 0;

    monitorIntervalRef.current = window.setInterval(() => {
      analyser.getByteTimeDomainData(dataArray);

      let sumSquares = 0;
      for (let i = 0; i < dataArray.length; i += 1) {
        const normalized = (dataArray[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }

      const rms = Math.sqrt(sumSquares / dataArray.length);
      maxRms = Math.max(maxRms, rms);
      elapsedFrames += 1;

      const SILENCE_THRESHOLD = 0.008;
      const SPEECH_THRESHOLD = 0.035;

      if (rms < SILENCE_THRESHOLD) {
        silentFrames += 1;
      } else if (rms >= SPEECH_THRESHOLD) {
        speechFrames += 1;
      } else {
        noiseFrames += 1;
      }

      updateAudioHealth({ maxRms, speechFrames, noiseFrames, silentFrames });

      if (elapsedFrames > 20 && maxRms < SILENCE_THRESHOLD) {
        setMicStatus("no_signal");
        setMicMessage("We can’t hear you. Check your microphone or unmute.");
      } else if (elapsedFrames > 35 && speechFrames < 4 && noiseFrames > 10) {
        setMicStatus("noise_only");
        setMicMessage(
          "We hear background noise, but no clear speech. Move closer to the mic."
        );
      } else if (speechFrames >= 4) {
        setMicStatus("speech_detected");
        setMicMessage("Voice detected. Keep recording clearly.");
      } else {
        setMicStatus("listening");
        setMicMessage("Listening for your voice...");
      }
    }, 100);
  }

  function stopAudioHealthMonitor() {
    if (monitorIntervalRef.current) {
      window.clearInterval(monitorIntervalRef.current);
      monitorIntervalRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    analyserRef.current = null;

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
  }

  useEffect(() => {
    loadIncident();
    loadWorkItems();

    let active = true;

    loadIncidentAudioDraft()
      .then((draft) => {
        if (!active || !draft?.blob) return;

        const restoredUrl = URL.createObjectURL(draft.blob);
        const restoredHealth = draft.audioHealth || {
          maxRms: 0,
          speechFrames: 0,
          noiseFrames: 0,
          silentFrames: 0,
        };

        setAudioBlob(draft.blob);
        replaceAudioUrl(restoredUrl);
        updateAudioHealth(restoredHealth);
        setAudioReviewed(false);

        if (restoredHealth.speechFrames >= 4 && restoredHealth.maxRms >= 0.015) {
          setMicStatus("speech_detected");
          setMicMessage(
            "Unsaved recording restored. Play it back and confirm it is clear before uploading."
          );
        } else {
          setMicStatus("blocked");
          setMicMessage(
            "The restored recording does not appear to contain usable speech. Please re-record."
          );
        }
      })
      .catch((err) => {
        console.error("Failed to restore incident audio draft", err);
      });

    return () => {
      active = false;
      stopAudioHealthMonitor();
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = "";
      }
    };
  }, [incidentId]);


  async function loadWorkItems() {
    if (incidentId === "mock") {
      setWorkItems([]);
      setWorkItemsError("");
      return;
    }

    const listWorkItems =
      workItemsApi.getIncidentWorkItems;

    if (!listWorkItems) {
      setWorkItems([]);
      setWorkItemsError(
        "The incident Work Items API is not connected in ../api/workItems."
      );
      return;
    }

    try {
      setWorkItemsLoading(true);
      setWorkItemsError("");

      const data = await listWorkItems(incidentId);
      const items = Array.isArray(data)
        ? data
        : data?.items || data?.work_items || [];

      setWorkItems(items);
    } catch (err) {
      console.error(err);
      setWorkItems([]);
      setWorkItemsError(
        err?.response?.data?.detail ||
          "Failed to load corrective actions."
      );
    } finally {
      setWorkItemsLoading(false);
    }
  }

  function openWorkItem(workItem) {
  if (!workItem) {
    return;
  }

  setSelectedWorkItem(workItem);
  setWorkItemDrawerError("");
  setWorkItemDrawerLoading(false);
}

  function closeWorkItemDrawer() {
    setSelectedWorkItem(null);
    setWorkItemDrawerLoading(false);
    setWorkItemDrawerError("");
  }

  function handleWorkItemUpdated(
    updatedWorkItem
  ) {
    setWorkItems((current) =>
      current.map((item) =>
        item.id === updatedWorkItem.id
          ? {
              ...item,
              ...updatedWorkItem,
            }
          : item
      )
    );

    setSelectedWorkItem(
      updatedWorkItem
    );
  }

  async function startRecording() {
    try {
      setAudioReviewed(false);
      setMicStatus("listening");
      setMicMessage("Listening for your voice...");
      updateAudioHealth({
        maxRms: 0,
        speechFrames: 0,
        noiseFrames: 0,
        silentFrames: 0,
      });

      replaceAudioUrl("");
      setAudioBlob(null);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      startAudioHealthMonitor(stream);

      let mimeType = "";
      if (MediaRecorder.isTypeSupported("audio/webm")) {
        mimeType = "audio/webm";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      } else if (MediaRecorder.isTypeSupported("audio/m4a")) {
        mimeType = "audio/m4a";
      }

      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const finalType = mediaRecorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: finalType });
        const localAudioUrl = URL.createObjectURL(blob);
        const finalHealth = audioHealthRef.current;

        setAudioBlob(blob);
        replaceAudioUrl(localAudioUrl);
        setAudioReviewed(false);

        try {
          await saveIncidentAudioDraft(blob, finalHealth);
          toast.success("Recording saved locally. Review it before uploading.");
        } catch (err) {
          console.error("Failed to save incident audio draft", err);
          toast.error(
            "Recording created, but local backup failed. Keep this page open and try again."
          );
        }

        if (finalHealth.speechFrames < 4 || finalHealth.maxRms < 0.015) {
          setMicStatus("blocked");
          setMicMessage(
            "This recording does not appear to contain usable speech. Please re-record."
          );
        } else {
          setMicStatus("speech_detected");
          setMicMessage(
            "Voice detected. Play the recording and confirm it is clear before uploading."
          );
        }
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      console.error(err);
      stopAudioHealthMonitor();
      setMicStatus("blocked");
      setMicMessage("Microphone access failed. Check browser permissions and try again.");
      toast.error("Microphone access failed");
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current?.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    stopAudioHealthMonitor();
    setRecording(false);
  }

  async function uploadReviewedAudio() {
    if (!audioBlob) {
      toast.error("Record a voice description first");
      return;
    }

    if (audioHealth.speechFrames < 4 || audioHealth.maxRms < 0.015) {
      toast.error("No usable speech was detected. Please re-record.");
      return;
    }

    if (!audioReviewed) {
      toast.error("Play back the recording and confirm your voice is clear first.");
      return;
    }

    try {
      setUploadingAudio(true);
      const finalType = audioBlob.type || "audio/webm";
      const extension = getFileExtension(finalType);
      const file = new File(
        [audioBlob],
        `incident-voice-${Date.now()}.${extension}`,
        { type: finalType }
      );

      if (incidentId === "mock") {
        setIncident((prev) => ({
          ...prev,
          status:
            prev.status === "capture_pending"
              ? "collecting_evidence"
              : prev.status,
          voice_captures: [
            ...(prev.voice_captures || []),
            {
              id: `voice-${Date.now()}`,
              audio_url: audioUrl,
              uploaded_at: new Date().toISOString(),
              uploaded_by: "current-user",
              duration_seconds: null,
            },
          ],
          activity: [
            ...(prev.activity || []),
            {
              id: `activity-${Date.now()}`,
              action_type: "voice_uploaded",
              created_at: new Date().toISOString(),
              metadata_json: { filename: file.name },
            },
          ],
        }));
      } else {
        await uploadIncidentAudio(incidentId, file);
        await loadIncident({ showPageLoader: false });
      }

      await clearIncidentAudioDraft();
      setAudioBlob(null);
      setAudioReviewed(false);
      setMicStatus("idle");
      setMicMessage("");
      replaceAudioUrl("");
      toast.success("Voice evidence uploaded");
    } catch (err) {
      console.error(err);
      toast.error(
        "Upload failed. Your recording is still saved locally; try uploading again."
      );
    } finally {
      setUploadingAudio(false);
    }
  }

  async function loadIncident({ showPageLoader = true } = {}) {
    try {
      if (showPageLoader) {
        setLoading(true);
      }

      // Temporary fallback while tenant DB tables/migrations are pending.
      if (incidentId === "mock") {
        const mock = {
          id: "mock",
          incident_number: "SAMPLE-NCR-001",
          title: "Incoming inspection: damaged valve housings",
          status: "capture_pending",
          workflow_type: "Incoming Material Inspection",
          evidence_type: "Incoming Inspection",
          severity: "Medium",
          operator_notes: "",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          voice_captures: [],
          evidence: [],
          activity: [
            {
              id: "a1",
              action_type: "incident_created",
              created_at: new Date().toISOString(),
              metadata_json: {},
            },
          ],
        };

        setIncident(mock);
        setOperatorNotes(mock.operator_notes || "");
        return;
      }

      const data = await getIncident(incidentId);
      setIncident(data);
      setOperatorNotes(data.operator_notes || "");
      const related = await getRelatedOperationalMemory(incidentId);
      setRelatedMemory(related.related_memory || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load incident");
    } finally {
      if (showPageLoader) {
        setLoading(false);
      }
    }
  }

  async function handleSaveNotes() {
    try {
      setSavingNotes(true);

      if (incidentId === "mock") {
        setIncident((prev) => ({
          ...prev,
          operator_notes: operatorNotes,
          updated_at: new Date().toISOString(),
          activity: [
            ...(prev.activity || []),
            {
              id: `mock-${Date.now()}`,
              action_type: "incident_updated",
              created_at: new Date().toISOString(),
              metadata_json: { field: "operator_notes" },
            },
          ],
        }));
        toast.success("Notes saved");
        return;
      }

      const updated = await updateIncident(incidentId, {
        operator_notes: operatorNotes,
      });

      setIncident((prev) => ({
        ...prev,
        ...updated,
      }));

      toast.success("Notes saved");
      await loadIncident({ showPageLoader: false });
    } catch (err) {
      console.error(err);
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  }

  
  async function handleEvidenceSelected(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      setUploadingEvidence(true);

      if (incidentId === "mock") {
        setIncident((prev) => ({
          ...prev,
          status:
            prev.status === "capture_pending"
              ? "collecting_evidence"
              : prev.status,
          evidence: [
            ...(prev.evidence || []),
            ...files.map((file) => ({
              id: `evidence-${file.name}-${Date.now()}`,
              file_url: URL.createObjectURL(file),
              file_type: file.type,
              original_filename: file.name,
              uploaded_at: new Date().toISOString(),
              uploaded_by: "current-user",
            })),
          ],
          activity: [
            ...(prev.activity || []),
            ...files.map((file) => ({
              id: `activity-${file.name}-${Date.now()}`,
              action_type: "evidence_uploaded",
              created_at: new Date().toISOString(),
              metadata_json: {
                filename: file.name,
                content_type: file.type,
              },
            })),
          ],
        }));

        toast.success("Evidence added");
        return;
      }

      for (const file of files) {
        await uploadIncidentEvidence(incidentId, file);
      }

      toast.success("Evidence uploaded");
      await loadIncident({ showPageLoader: false });
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload evidence");
    } finally {
      setUploadingEvidence(false);
      e.target.value = "";
    }
  }

  function formatDate(value) {
    if (!value) return "—";
    return new Date(value).toLocaleString();
  }

  function statusLabel(status) {
    return String(status || "")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function activityLabel(activity) {
    const type = activity.action_type;

    if (type === "incident_created") return "Incident workspace created";
    if (type === "voice_uploaded") return "Voice evidence uploaded";
    if (type === "evidence_uploaded") {
      return `Evidence uploaded${
        activity.metadata_json?.filename
          ? `: ${activity.metadata_json.filename}`
          : ""
      }`;
    }
    if (type === "incident_updated") return "Incident updated";
    if (type === "status_changed") return "Status changed";

    return statusLabel(type);
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 text-center text-slate-500">
        Loading incident workspace...
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 text-center">
        <p className="text-slate-500 mb-4">Incident not found.</p>
        <button
          onClick={() => navigate("/secure/incidents")}
          className="px-4 py-2 rounded-lg bg-primary text-white font-semibold"
        >
          Back to My Incidents
        </button>
      </div>
    );
  }

  const voiceCaptures = incident.voice_captures || [];
  const evidence = incident.evidence || [];
  const activity = incident.activity || [];
  const canSubmitForProcessing =
  voiceCaptures.length > 0 &&
  evidence.length > 0 &&
  incident.status !== "processing" &&
  incident.status !== "under_review" &&
  incident.status !== "approved" &&
  incident.status !== "closed";

  const canDeleteAttachments = [
    "capture_pending",
    "collecting_evidence",
    "ready_for_processing",
  ].includes(incident.status);

  return (
    <div className="px-6 lg:px-8 py-6 space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
        <button
          onClick={() => navigate(incidentId === "mock" ? "/onboarding/welcome" : "/secure/my-incidents")}
          className="text-sm text-slate-500 hover:text-primary mb-4"
        >
          ← Back to My Incidents
        </button>

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
              {incidentId === "mock" ? "Guided sample workspace" : "Incident Workspace"}
            </div>

            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {incident.incident_number}
            </h1>

            <p className="text-slate-500 mt-2">
              Capture first. Add evidence later. AI processing starts only after explicit submission.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Workflow Type
                </div>
                <div className="mt-1 font-semibold text-slate-900 dark:text-white">
                  {incident.workflow_type || "Not provided"}
                </div>
              </div>

              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Evidence Type
                </div>
                <div className="mt-1 font-semibold text-slate-900 dark:text-white">
                  {incident.evidence_type || "Not provided"}
                </div>
              </div>

              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Severity
                </div>
                <div className="mt-1 font-semibold text-slate-900 dark:text-white">
                  {incident.severity || "Not provided"}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start md:items-end gap-2">
            <span className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold uppercase tracking-wider">
              {statusLabel(incident.status)}
            </span>

            <span className="text-xs text-slate-500">
              Last updated: {formatDate(incident.updated_at)}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Main workspace */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {/* Voice Capture */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
            <div className="mb-5">
              <h2 className="font-bold text-slate-900 dark:text-white">
                {incident.workflow_type || "Incident"} Capture
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Record the operator's raw description. Use the checklist below to capture the key details.
              </p>
              <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-4 dark:border-blue-900/40 dark:bg-blue-950/30">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-blue-900 dark:text-blue-200">
                  What to mention for {incident.workflow_type || "this incident"}
                </div>

                <ul className="space-y-1 text-sm text-blue-900 dark:text-blue-100">
                  {(workflowGuidanceChecklist[incident.workflow_type] ||
                    workflowGuidanceChecklist.Other
                  ).map((item) => (
                    <li key={item} className="flex gap-2">
                      <span>✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mb-8 flex flex-col items-center">
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={uploadingAudio}
                className={`flex h-24 w-24 items-center justify-center rounded-full text-xl text-white shadow-lg transition ${
                  recording
                    ? "bg-red-500 animate-pulse"
                    : uploadingAudio
                    ? "bg-slate-400 cursor-not-allowed"
                    : "bg-primary"
                }`}
              >
                🎤
              </button>

              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                {recording
                  ? "Recording..."
                  : uploadingAudio
                  ? "Uploading recording..."
                  : audioBlob
                  ? "Recording ready"
                  : "Tap to record"}
              </p>

              {audioUrl && (
                <div className="mt-4 w-full max-w-md space-y-3">
                  <audio controls src={audioUrl} className="w-full" />

                  {micMessage && (
                    <div
                      className={`rounded-lg border px-3 py-2 text-sm ${
                        micStatus === "speech_detected"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200"
                          : micStatus === "blocked" ||
                            micStatus === "no_signal" ||
                            micStatus === "noise_only"
                          ? "border-red-200 bg-red-50 text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200"
                          : "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-200"
                      }`}
                    >
                      {micMessage}
                    </div>
                  )}

                  <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={audioReviewed}
                      onChange={(event) => setAudioReviewed(event.target.checked)}
                      className="mt-1"
                    />
                    <span>
                      I played back the recording and can clearly hear the operator's voice.
                    </span>
                  </label>

                  <button
                    type="button"
                    onClick={uploadReviewedAudio}
                    disabled={
                      uploadingAudio ||
                      !audioReviewed ||
                      audioHealth.speechFrames < 4 ||
                      audioHealth.maxRms < 0.015
                    }
                    className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {uploadingAudio ? "Uploading recording..." : "Upload reviewed recording"}
                  </button>

                  <p className="text-xs text-slate-500">
                    This draft is stored locally for this incident until the upload succeeds.
                  </p>
                </div>
              )}

              {recording && micMessage && (
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                  {micMessage}
                </p>
              )}

            </div>

            {voiceCaptures.length === 0 ? (
              <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-8 text-center text-slate-500">
                No voice evidence added yet.
              </div>
            ) : (
              <div className="space-y-3">
                {voiceCaptures.map((voice) => (
                  <div
                    key={voice.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800"
                  >
                    <div>
                      <p className="font-semibold text-sm text-slate-900 dark:text-white">
                        Voice Recording
                      </p>
                      <p className="text-xs text-slate-500">
                        Uploaded {formatDate(voice.uploaded_at)}
                      </p>
                    </div>

                    {voice.audio_url && (
                      <audio controls src={voice.audio_url} className="max-w-xs" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Evidence */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-bold text-slate-900 dark:text-white">
                  Evidence
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Add photos, PDFs, receipts, packing slips, or supporting documents.
                </p>
              </div>

              <button
                onClick={() => evidenceInputRef.current?.click()}
                disabled={uploadingEvidence}
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50"
              >
                {uploadingEvidence ? "Uploading..." : "Add Evidence"}
              </button>

              <input
                ref={evidenceInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                className="hidden"
                onChange={handleEvidenceSelected}
              />
            </div>

            {evidence.length === 0 ? (
              <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-8 text-center text-slate-500">
                No evidence attached yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {evidence.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-slate-900 dark:text-white truncate">
                          {item.original_filename || "Evidence File"}
                        </p>

                        <p className="text-xs text-slate-500 mt-1">
                          {item.file_type || "file"} · {formatDate(item.uploaded_at)}
                        </p>
                      </div>

                      {canDeleteAttachments && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm("Delete this evidence file?")) return;

                            try {
                              await deleteIncidentEvidence(incident.id, item.id);
                              toast.success("Evidence deleted");
                              await loadIncident({ showPageLoader: false });
                            } catch (err) {
                              console.error(err);
                              toast.error(
                                err?.response?.data?.detail ||
                                  "Failed to delete evidence"
                              );
                            }
                          }}
                          className="shrink-0 text-xs font-bold text-red-600 hover:text-red-700"
                        >
                          Delete
                        </button>
                      )}
                    </div>

                    {item.file_type?.startsWith("image/") && item.file_url && (
                      <img
                        src={item.file_url}
                        alt={item.original_filename || "evidence"}
                        className="mt-3 h-32 w-full object-cover rounded-lg border border-slate-200 dark:border-slate-700"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
            </div>


          {/* Corrective Actions */}
          <CorrectiveActionsPanel
            workItems={workItems}
            loading={workItemsLoading}
            error={workItemsError}
            onRefresh={loadWorkItems}
            onOpenWorkItem={openWorkItem}
          />

          {/* Notes */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
            <h2 className="font-bold text-slate-900 dark:text-white">
              Operator Notes
            </h2>

            <p className="text-sm text-slate-500 mt-1 mb-4">
              Optional context. These notes can be updated before formal processing.
            </p>

            <textarea
              value={operatorNotes}
              onChange={(e) => setOperatorNotes(e.target.value)}
              rows={5}
              placeholder="Add context, supplier info, containment actions, or anything the reviewer should know..."
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-3 focus:ring-primary focus:border-primary"
            />

            <div className="flex justify-end mt-4">
              <button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-semibold disabled:opacity-50"
              >
                {savingNotes ? "Saving..." : "Save Notes"}
              </button>
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          <RelatedOperationalMemory items={relatedMemory} />

          {/* Readiness */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
            <h2 className="font-bold text-slate-900 dark:text-white">
              Processing Readiness
            </h2>

            <div className="space-y-3 mt-5">
              <ReadinessItem
                complete={voiceCaptures.length > 0}
                label="Voice capture added"
              />
              <ReadinessItem
                complete={evidence.length > 0}
                label="Evidence attached"
              />
              <ReadinessItem
                complete={Boolean(operatorNotes?.trim())}
                label="Operator notes optional"
              />
            </div>

            <div className="mt-6 space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  To
                </label>
                <input
                  type="text"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="reviewer@example.com"
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  CC
                </label>
                <input
                  type="text"
                  value={emailCc}
                  onChange={(e) => setEmailCc(e.target.value)}
                  placeholder="manager@example.com, quality@example.com"
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-2 text-sm"
                />
              </div>
            </div>

            <button
                disabled={!canSubmitForProcessing}
                onClick={async () => {
                    try {
                    if (incident.id === "mock") {
                      await completeGuidedSample();
                      toast.success("Guided sample completed — no live incident used");
                      navigate("/onboarding/welcome", { replace: true });
                      return;
                    }
                    await submitIncidentForProcessing(incident.id, {
                      to_emails: emailTo
                        ? emailTo.split(",").map((x) => x.trim()).filter(Boolean)
                        : [],
                      cc_emails: emailCc
                        ? emailCc.split(",").map((x) => x.trim()).filter(Boolean)
                        : [],
                    });
                    toast.success("Incident submitted for processing");
                    await loadIncident({ showPageLoader: false });
                    } catch (err) {
                    console.error(err);
                    toast.error(err?.response?.data?.detail || "Failed to submit incident");
                    }
                }}
                className={`mt-6 w-full py-3 rounded-xl font-bold transition ${
                    canSubmitForProcessing
                    ? "bg-primary text-white hover:opacity-90"
                    : "bg-slate-200 dark:bg-slate-800 text-slate-500 cursor-not-allowed"
                }`}
                >
                Submit for Processing
            </button>

            {incident.status === "under_review" && (
              <button
                type="button"
                onClick={() => setShowNcrReview(true)}
                className="mt-3 w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold"
              >
                Review NCR
              </button>
            )}

            <p className="text-xs text-slate-500 mt-3">
              AI transcription, NCR generation, and reviewer email will only run after explicit submission.
            </p>
          </div>
          {incident.status === "under_review" && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
              <h2 className="font-bold text-slate-900 dark:text-white">
                Reviewer Decision
              </h2>

              <p className="text-sm text-slate-500 mt-1">
                Review the generated artifact, evidence, and operator notes before approving or rejecting.
              </p>

              <textarea
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                rows={4}
                placeholder="Add reviewer comments..."
                className="mt-4 w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm p-3 focus:ring-primary focus:border-primary"
              />

              <div className="grid grid-cols-2 gap-3 mt-4">
                <button
                  disabled={reviewing}
                  onClick={async () => {
                    try {
                      setReviewing(true);
                      await approveIncident(incident.id, {
                        review_comments: reviewComments,
                      });
                      toast.success("Incident approved");
                      await loadIncident({ showPageLoader: false });
                    } catch (err) {
                      console.error(err);
                      toast.error(err?.response?.data?.detail || "Failed to approve incident");
                    } finally {
                      setReviewing(false);
                    }
                  }}
                  className="py-3 rounded-xl bg-emerald-600 text-white font-bold disabled:opacity-50"
                >
                  Approve
                </button>

                <button
                  disabled={reviewing}
                  onClick={async () => {
                    try {
                      setReviewing(true);
                      await rejectIncident(incident.id, {
                        review_comments: reviewComments,
                      });
                      toast.success("Incident rejected");
                      await loadIncident({ showPageLoader: false });
                    } catch (err) {
                      console.error(err);
                      toast.error(err?.response?.data?.detail || "Failed to reject incident");
                    } finally {
                      setReviewing(false);
                    }
                  }}
                  className="py-3 rounded-xl bg-red-600 text-white font-bold disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            </div>
          )}

          {["approved", "rejected"].includes(incident.status) && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
              <h2 className="font-bold text-slate-900 dark:text-white">
                Review Complete
              </h2>

              <p className="text-sm text-slate-500 mt-2">
                Status: {statusLabel(incident.status)}
              </p>

              {incident.review_comments && (
                <div className="mt-4 rounded-lg bg-slate-50 dark:bg-slate-800 p-4">
                  <p className="text-xs font-bold uppercase text-slate-500 mb-1">
                    Reviewer Comments
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-200">
                    {incident.review_comments}
                  </p>
                </div>
              )}
              {incident.status === "approved" && (
              <button
              type="button"
              onClick={() => setShowNcrReview(true)}
              className="mt-4 w-full rounded-xl border border-slate-300 px-4 py-3 font-bold"
              >
              {incident.status === "approved"
                ? "View Finalized NCR"
                : "View Rejected NCR"}
              </button>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
            <h2 className="font-bold text-slate-900 dark:text-white">
              Activity Timeline
            </h2>

            {activity.length === 0 ? (
              <p className="text-sm text-slate-500 mt-4">
                No activity yet.
              </p>
            ) : (
              <div className="mt-5 space-y-4">
                {activity.map((item) => (
                  <div key={item.id} className="flex gap-3">
                    <div className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                        {activityLabel(item)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDate(item.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <WorkItemDetailsDrawer
        workItem={selectedWorkItem}
        incidentNumber={incident.incident_number}
        loading={workItemDrawerLoading}
        error={workItemDrawerError}
        onClose={closeWorkItemDrawer}
        onWorkItemUpdated={
          handleWorkItemUpdated
        }
      />

      {showNcrReview && (
        <NcrReviewModal
          incidentId={incident.id}
          incidentNumber={incident.incident_number}
          onClose={() => setShowNcrReview(false)}
        />
      )}
    </div>
  );
}

function RelatedOperationalMemory({ items }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs font-bold uppercase tracking-wider text-primary">Operational Memory</p>
      <h2 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">Related historical records</h2>
      {!items.length ? (
        <p className="mt-3 text-sm text-slate-500">No related historical records were found yet.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {items.map((item) => <RelatedMemoryRecord key={item.id || item.record_id} item={item} />)}
        </div>
      )}
    </section>
  );
}

function RelatedMemoryRecord({ item }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="overflow-hidden rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/20">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 p-3 text-left hover:bg-amber-100/60 dark:hover:bg-amber-900/20"
      >
        <span className="material-symbols-outlined text-lg text-amber-800 dark:text-amber-200">
          {expanded ? "expand_more" : "chevron_right"}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-900 dark:text-white">{item.record_id}</span>
          {item.record_title && <span className="mt-0.5 block truncate text-xs text-slate-600 dark:text-slate-300">{item.record_title}</span>}
        </span>
        <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">Inferred</span>
      </button>

      {expanded && (
        <div className="border-t border-amber-200 px-4 pb-4 pt-3 dark:border-amber-900/60">
          <p className="text-sm text-slate-600 dark:text-slate-300">{item.explanation}</p>
          {item.shared_signals?.length > 0 && <p className="mt-3 text-xs text-slate-600 dark:text-slate-300"><span className="font-semibold">Shared signals:</span> {item.shared_signals.join(", ")}</p>}
          {item.source_facts && <p className="mt-2 text-xs text-slate-600 dark:text-slate-300"><span className="font-semibold">Source facts:</span> {item.source_facts.status}{item.source_facts.workflow_type ? ` · ${item.source_facts.workflow_type}` : ""}</p>}
          {item.fluxonix_interpretation?.decision && <p className="mt-2 text-xs text-slate-600 dark:text-slate-300"><span className="font-semibold">Fluxonix interpretation:</span> {item.fluxonix_interpretation.decision}</p>}
          {item.actions?.length > 0 && <p className="mt-2 text-xs text-slate-600 dark:text-slate-300"><span className="font-semibold">Recorded actions:</span> {item.actions.map((action) => `${action.title} (${action.verification_state})`).join("; ")}</p>}
          {item.participants?.length > 0 && <p className="mt-2 text-xs text-slate-600 dark:text-slate-300"><span className="font-semibold">Operational participation:</span> {item.participants.map((participant) => `${participant.label}: ${participant.participation.join(", ")}`).join("; ")}</p>}
          {item.memory_gaps?.map((gap) => <p key={gap.description} className="mt-2 text-xs text-slate-600 dark:text-slate-300"><span className="font-semibold">Memory gap:</span> {gap.description}</p>)}
          {item.explicit_source_references?.map((reference) => (
            <p key={reference.record_id} className="mt-3 text-xs text-slate-500">Explicit in {item.record_id}: {reference.relation.replaceAll("_", " ")} {reference.record_id}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function ReadinessItem({ complete, label }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={`material-symbols-outlined text-lg ${
          complete ? "text-emerald-500" : "text-slate-400"
        }`}
      >
        {complete ? "check_circle" : "radio_button_unchecked"}
      </span>
      <span
        className={`text-sm ${
          complete
            ? "text-slate-800 dark:text-slate-100"
            : "text-slate-500"
        }`}
      >
        {label}
      </span>
    </div>
  );
}

function CorrectiveActionsPanel({
  workItems,
  loading,
  error,
  onRefresh,
  onOpenWorkItem,
}) {
  const now = new Date();

  const summary = workItems.reduce(
    (acc, item) => {
      const status = String(item.status || "open").toLowerCase();
      const dueDate = item.due_date ? new Date(item.due_date) : null;
      const overdue =
        dueDate &&
        dueDate < now &&
        !["complete", "verified"].includes(status);

      acc.total += 1;
      if (["open", "in_progress", "blocked"].includes(status)) acc.active += 1;
      if (overdue) acc.overdue += 1;
      if (status === "blocked") acc.blocked += 1;
      if (status === "complete" && !item.verified_at) acc.awaitingVerification += 1;
      if (status === "verified" || item.verified_at) acc.verified += 1;
      return acc;
    },
    {
      total: 0,
      active: 0,
      overdue: 0,
      blocked: 0,
      awaitingVerification: 0,
      verified: 0,
    }
  );

  const sortedItems = [...workItems].sort((a, b) => {
    const rank = {
      blocked: 0,
      complete: 1,
      in_progress: 2,
      open: 3,
      verified: 4,
    };

    const aRank = rank[a.status] ?? 5;
    const bRank = rank[b.status] ?? 5;
    if (aRank !== bRank) return aRank - bRank;

    const aDue = a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    const bDue = b.due_date ? new Date(b.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    return aDue - bDue;
  });

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
            Post-finalization execution
          </p>
          <h2 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            Corrective Actions
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Track ownership, due dates, completion, and reviewer verification for this incident.
          </p>
        </div>

        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <ActionMetric label="Total" value={summary.total} />
        <ActionMetric label="Active" value={summary.active} />
        <ActionMetric label="Overdue" value={summary.overdue} tone={summary.overdue ? "danger" : "default"} />
        <ActionMetric label="Blocked" value={summary.blocked} tone={summary.blocked ? "warning" : "default"} />
        <ActionMetric label="Verify" value={summary.awaitingVerification} tone={summary.awaitingVerification ? "info" : "default"} />
        <ActionMetric label="Verified" value={summary.verified} tone="success" />
      </div>

      {error ? (
        <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30">
          <p className="text-sm font-semibold text-red-900 dark:text-red-200">
            Corrective actions unavailable
          </p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      ) : loading && workItems.length === 0 ? (
        <div className="mt-5 rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 dark:border-slate-700">
          Loading corrective actions...
        </div>
      ) : workItems.length === 0 ? (
        <div className="mt-5 rounded-xl border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
          <p className="font-semibold text-slate-800 dark:text-slate-100">
            No corrective actions created
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Actions generated during NCR finalization will appear here.
          </p>
        </div>
      ) : (
        <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
          <div className="divide-y divide-slate-200 dark:divide-slate-800">
            {sortedItems.map((item) => {
              const status = String(item.status || "open").toLowerCase();
              const dueDate = item.due_date ? new Date(item.due_date) : null;
              const overdue =
                dueDate &&
                dueDate < now &&
                !["complete", "verified"].includes(status);
              const owner =
                item.assigned_to_name ||
                item.assigned_to_email ||
                "Unassigned";

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onOpenWorkItem(item)}
                  className="grid w-full gap-4 px-4 py-4 text-left transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 dark:hover:bg-slate-800/70 md:grid-cols-[minmax(0,1fr)_150px_125px_120px] md:items-center"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-semibold text-slate-900 dark:text-white">
                        {item.title || "Untitled corrective action"}
                      </p>
                      <WorkItemStatusBadge item={item} />
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-500">
                      {item.description || "No description provided."}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Owner</p>
                    <p className="mt-1 truncate text-sm font-medium text-slate-700 dark:text-slate-200">{owner}</p>
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Due</p>
                    <p className={`mt-1 text-sm font-medium ${overdue ? "text-red-600 dark:text-red-400" : "text-slate-700 dark:text-slate-200"}`}>
                      {dueDate ? dueDate.toLocaleDateString() : "No due date"}
                      {overdue ? " · Overdue" : ""}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Priority</p>
                    <div className="mt-1"><PriorityBadge priority={item.priority} /></div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

function ActionMetric({ label, value, tone = "default" }) {
  const tones = {
    default: "border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-white",
    danger: "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200",
    warning: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
    info: "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200",
    success: "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200",
  };

  return (
    <div className={`rounded-lg border p-3 ${tones[tone] || tones.default}`}>
      <p className="text-xs font-bold uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-1 text-xl font-bold">{value}</p>
    </div>
  );
}

function WorkItemStatusBadge({ item }) {
  const status = String(item?.status || "open").toLowerCase();
  const verified = status === "verified" || Boolean(item?.verified_at);
  const awaitingVerification = status === "complete" && !item?.verified_at;

  let label = formatEnumLabel(status);
  let classes = "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";

  if (verified) {
    label = "Verified";
    classes = "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300";
  } else if (awaitingVerification) {
    label = "Awaiting verification";
    classes = "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300";
  } else if (status === "blocked") {
    classes = "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300";
  } else if (status === "in_progress") {
    classes = "bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-300";
  }

  return <span className={`rounded-full px-2 py-1 text-xs font-semibold ${classes}`}>{label}</span>;
}

function PriorityBadge({ priority }) {
  const normalized = String(priority || "medium").toLowerCase();
  const classes = {
    low: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    medium: "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300",
    high: "bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-300",
    urgent: "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300",
  };

  return (
    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${classes[normalized] || classes.medium}`}>
      {formatEnumLabel(normalized)}
    </span>
  );
}

function formatEnumLabel(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function WorkItemDetailsDrawer({
  workItem,
  incidentNumber,
  loading = false,
  error = "",
  onClose,
}) {
  const drawerRef = useRef(null);
  const [verificationNotes, setVerificationNotes] = useState("");
  const [verificationAction, setVerificationAction] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const isOpen = Boolean(workItem || loading || error);

  useEffect(() => {
    setVerificationNotes(
      workItem?.verification_notes || ""
    );
    setVerificationAction("");
    setVerificationError("");
  }, [workItem?.id]);

  const canReview = workItem?.status === "complete";
  
  async function handleVerify() {
    setVerificationError("");
    setVerificationAction("verify");

    try {
      const updatedWorkItem =
        await workItemsApi.verifyWorkItem(
          workItem.id,
          verificationNotes
        );

      onWorkItemUpdated?.(
        updatedWorkItem
      );
    } catch (error) {
      setVerificationError(
        error?.response?.data?.detail ||
          "Unable to verify the work item."
      );
    } finally {
      setVerificationAction("");
    }
  }

  async function handleReturnForRework() {
    const notes = verificationNotes.trim();

    if (!notes) {
      setVerificationError(
        "Add verification notes explaining what additional work is required."
      );
      return;
    }

    setVerificationError("");
    setVerificationAction("return");

    try {
      const updatedWorkItem =
        await workItemsApi.returnWorkItem(
          workItem.id,
          notes
        );

      onWorkItemUpdated?.(
        updatedWorkItem
      );
    } catch (error) {
      setVerificationError(
        error?.response?.data?.detail ||
          "Unable to return the work item."
      );
    } finally {
      setVerificationAction("");
    }
  }

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const owner =
    workItem?.assigned_to_name ||
    workItem?.assigned_to_email ||
    "Unassigned";

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-labelledby="work-item-drawer-title">
      <button type="button" aria-label="Close work item details" onClick={onClose} className="absolute inset-0 bg-black/30" />

      <aside ref={drawerRef} className="relative z-10 flex h-full w-full max-w-lg flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">Corrective Work Item</p>
            <h2 id="work-item-drawer-title" className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">
              {loading ? "Loading work item" : error ? "Work item unavailable" : workItem?.title || "Untitled work item"}
            </h2>
            {!loading && !error && workItem && incidentNumber && (
              <p className="mt-1 text-sm text-slate-500">Created from NCR {incidentNumber}</p>
            )}
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white" aria-label="Close work item details">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex min-h-48 items-center justify-center"><p className="text-sm text-slate-500">Loading work item...</p></div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30">
              <p className="text-sm font-semibold text-red-900 dark:text-red-200">Unable to open work item</p>
              <p className="mt-1 text-sm leading-6 text-red-700 dark:text-red-300">{error}</p>
            </div>
          ) : workItem ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <PriorityBadge priority={workItem.priority} />
                <WorkItemStatusBadge item={workItem} />
              </div>

              <dl className="mt-6 divide-y divide-slate-200 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
                <DrawerField label="Owner" value={owner} />
                <DrawerField label="Due date" value={workItem.due_date ? new Date(workItem.due_date).toLocaleDateString() : "No due date"} />
                <DrawerField label="Department" value={workItem.assigned_to_department || "Not specified"} />
                <DrawerField label="Completed" value={workItem.completed_at ? new Date(workItem.completed_at).toLocaleString() : "Not completed"} />
                <DrawerField label="Verified" value={workItem.verified_at ? new Date(workItem.verified_at).toLocaleString() : "Not verified"} />
              </dl>

              <DrawerSection title="Description" value={workItem.description || "No additional description was provided."} />
              {workItem.completion_notes && <DrawerSection title="Completion notes" value={workItem.completion_notes} />}
              {workItem.verification_notes && <DrawerSection title="Verification notes" value={workItem.verification_notes} />}
              {canReview && (
                <section className="mt-6 border-t border-slate-200 pt-6 dark:border-slate-700">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                      Verification decision
                    </h3>

                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Review the completion notes and evidence,
                      then verify the action or return it for
                      additional work.
                    </p>
                  </div>

                  <label className="block">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Verification notes
                    </span>

                    <textarea
                      rows={4}
                      value={verificationNotes}
                      onChange={(event) =>
                        setVerificationNotes(
                          event.target.value
                        )
                      }
                      disabled={Boolean(
                        verificationAction
                      )}
                      placeholder="Add reviewer notes..."
                      className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                  </label>

                  {verificationError && (
                    <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                      {verificationError}
                    </div>
                  )}

                  <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={handleReturnForRework}
                      disabled={Boolean(
                        verificationAction
                      )}
                      className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      {verificationAction === "return"
                        ? "Returning..."
                        : "Return to In Progress"}
                    </button>

                    <button
                      type="button"
                      onClick={handleVerify}
                      disabled={Boolean(
                        verificationAction
                      )}
                      className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {verificationAction === "verify"
                        ? "Verifying..."
                        : "Verify and Close"}
                    </button>
                  </div>
                </section>
              )}
              {workItem.status === "verified" && (
                <section className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/30">
                  <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                    Verified
                  </div>

                  <div className="mt-2 text-sm text-emerald-700 dark:text-emerald-400">
                    Verified by{" "}
                    {workItem.verified_by_name ||
                      "Reviewer"}
                    {workItem.verified_at
                      ? ` on ${formatDateTime(
                          workItem.verified_at
                        )}`
                      : ""}
                  </div>

                  {workItem.verification_notes && (
                    <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                      {workItem.verification_notes}
                    </p>
                  )}
                </section>
              )}
            </>
          ) : null}
        </div>

        <div className="border-t border-slate-200 px-6 py-4 dark:border-slate-800">
          <button type="button" onClick={onClose} className="w-full rounded-xl border border-slate-300 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900">Close</button>
        </div>
      </aside>
    </div>
  );
}


function DrawerField({ label, value }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-4 px-4 py-3">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-sm font-medium text-slate-900 dark:text-white">{value}</dd>
    </div>
  );
}

function DrawerSection({ title, value }) {
  return (
    <section className="mt-6">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h3>
      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-600 dark:text-slate-300">{value}</p>
    </section>
  );
}
